// Film Plan View Types

import type { SxProps, Theme } from '@mui/material';
import type { CellSizeMode } from '@/components/EntityCell';
import type { Scene } from '@/types/fountain';

// Save status for API operations
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Extended Scene type with position for DnD
export interface SceneWithPosition extends Scene {
    position: number;
    sceneTitle?: string; // Backend returns "Scene 1", "Scene 2", etc.
}

// View type for the plan
export type FilmPlanViewType = 'scene-cards' | 'episode-cards';

export type { CellSizeMode };

// Visibility settings for film plan cards
export interface FilmPlanVisibilitySettings {
    showScenes: boolean;
    loglines: boolean;
    synopsesActs: boolean;
    synopsesEpisodes: boolean;
    synopsesScenes: boolean;
    locations: boolean;
    characters: boolean;
    tags: boolean;
    lineTags: boolean;
    lineFocus: boolean;
    columns?: { acts: boolean; episodes: boolean; scenes: boolean };
}

export const DEFAULT_FILM_PLAN_VISIBILITY: FilmPlanVisibilitySettings = {
    showScenes: false,
    loglines: true,
    synopsesActs: true,
    synopsesEpisodes: true,
    synopsesScenes: true,
    locations: true,
    characters: true,
    tags: true,
    lineTags: true,
    lineFocus: true,
};

export const DEFAULT_EDITOR_VISIBILITY: FilmPlanVisibilitySettings = {
    ...DEFAULT_FILM_PLAN_VISIBILITY,
    columns: { acts: true, episodes: true, scenes: true },
};

// Storage keys
export const FILM_PLAN_VIEW_MODE_KEY = 'film-plan-view-mode';
export const FILM_PLAN_ONE_LINER_KEY = 'film-plan-one-liner';
export const FILM_PLAN_SHOW_SCENES_KEY = 'film-plan-show-scenes';
export const FILM_PLAN_CELL_SIZE_KEY = 'film-plan-cell-size';
export const FILM_PLAN_VISIBILITY_KEY = 'film-plan-visibility';
export const FILM_PLAN_COLUMNS_COUNT_KEY = 'film-plan-columns-count';

export const COLUMNS_SLIDER = {
    MIN: 4,
    MAX: 12,
    DEFAULT: 4,
} as const;

// Grid dimensions for 10×12 cards on 2304×1296 screen
// Reference screen: 2304×1296 should fit 10 columns × 12 rows = 120 cards
export const GRID_CONSTANTS = {
    // Target grid size
    TARGET_COLUMNS: 10,
    TARGET_ROWS: 12,
    // Reference screen dimensions
    REFERENCE_WIDTH: 2304,
    REFERENCE_HEIGHT: 1296,
    // Header height
    HEADER_HEIGHT: 56,
    // Padding and gaps
    CONTAINER_PADDING: 16, // px on each side
    COLUMN_GAP: 16, // gap between columns
    ROW_GAP: 8, // gap between rows
} as const;

// Calculate card dimensions based on reference screen
// Available width: 2304 - 32 (padding) - 72 (9 gaps) = 2200px → 220px per card
// Available height: 1296 - 56 (header) - 32 (padding) - 88 (11 gaps) = 1120px → 93px per card
export const CARD_DIMENSIONS = {
    WIDTH: 320, // px per column
    MIN_HEIGHT: 90, // px minimum row height for episode-cards view
} as const;

// Vertical gap between the flat act/episode/scene cards in a column (issue #1799)
export const CARD_STACK_GAP = 8; // px

// Text styles for expandable/truncatable content
export const getTextContentStyles = (cellSize: CellSizeMode): Record<string, unknown> => ({
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    ...(cellSize === 'compact' && {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        WebkitBoxOrient: 'vertical' as const,
    }),
    ...(cellSize === 'medium' && {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical' as const,
    }),
});

// Card hover styles (stable references — no functions to avoid new objects per render).
// Includes the reveal rules for CardHoverControls and empty-field placeholders.
export const CARD_HOVER_STYLES: SxProps<Theme> = {
    transition: 'all 0.15s ease',
    '&:hover': {
        borderColor: (theme: Theme) => theme.custom.c62,
    },
    '&:hover .card-hover-controls': { opacity: 1 },
    '&:hover .placeholder-text': { opacity: 1 },
};
